/* =============================================================================
 * LAS ACTUALIZACIONES DE LA PLATAFORMA, VERSIONADAS CON EL CÓDIGO
 * -----------------------------------------------------------------------------
 * Es la ÚNICA fuente de lo que se anuncia dentro de la plataforma: el aviso
 * flotante de las pantallas de inicio y la sección "Actualizaciones" del perfil.
 * Nada vive en Firestore ni hay panel para editarlo: una actualización se
 * anuncia en la misma rama que la trae, y se revisa con ella.
 *
 * CADA ENTRADA:
 *   · id       estable y único. Es lo que se guarda en `usuarios/{uid}.novedadesVistas`:
 *              si se cambia, a todo el mundo le vuelve a salir.
 *   · fecha    "AAAA-MM-DD". Una cuenta creada DESPUÉS de ese día no ve el aviso
 *              flotante (no le avisamos de un cambio que para ella no es cambio);
 *              la encuentra en "Actualizaciones".
 *   · para     "patron" o "trabajador".
 *   · requiere módulo que la cuenta tiene que usar para verla, o null. Hoy solo se
 *              entiende "flotilla" (el patrón con al menos una unidad registrada, o
 *              el trabajador cuyo patrón la tenga). Un valor desconocido NO se
 *              enseña a nadie: ver `src/logica/novedades.js`.
 *   · titulo, resumen (una línea) y puntos (textos cortos).
 *
 * ⚠️ EL TEXTO TIENE QUE SER CIERTO SEGÚN EL CÓDIGO. Antes de añadir una entrada,
 * cada punto se contrasta con lo que la plataforma hace; si no, se corrige el
 * texto o se espera. Y una entrada nueva la aprueba Iván antes de entrar aquí.
 *
 * CommonJS, exportado una a una, como src/logica/*.js: lo leen la aplicación y
 * las pruebas en Node.
 * ========================================================================== */

const NOVEDADES = [
  {
    id: "2026-10-05-accidentes-patron",
    fecha: "2026-10-05",
    para: "patron",
    requiere: "flotilla",
    titulo: "Novedades en Flotilla: accidentes",
    resumen: "Registro más rápido y avisos de todos los accidentes.",
    puntos: [
      "Tu equipo registra un accidente en segundos: elige qué pasó, contesta si hay personas lesionadas y listo. Si va en un viaje, la unidad se toma sola.",
      "Activa los avisos en tu celular y te avisaremos de todos los accidentes, con o sin personas lesionadas.",
      'En "Requieren tu atención" ves los accidentes en orden, del más reciente al más antiguo, con una etiqueta que indica si hubo personas lesionadas.',
      "El detalle reúne tipo de evento, relato del trabajador, lesionados, fotos, video, otro conductor, testigos y autoridades.",
      'Tu equipo puede completar el accidente durante 7 días desde "Mis accidentes".',
      "Si tu celular es Samsung u otro Android, revisa que Chrome no esté en suspensión (Ajustes → Batería → Límites de uso en segundo plano) para que te lleguen los avisos.",
    ],
  },
  {
    id: "2026-10-05-accidentes-trabajador",
    fecha: "2026-10-05",
    para: "trabajador",
    requiere: "flotilla",
    titulo: "Novedades: registro de accidentes",
    resumen: "Registrar un accidente ahora es más rápido.",
    puntos: [
      "Registrar un accidente ahora es más rápido: eliges qué pasó, contestas si hay personas lesionadas y listo. Si vas en un viaje, la unidad se pone sola.",
      'Nuevo "Mis accidentes": durante 7 días puedes agregar fotos, video, testigos y, si fue con una unidad, los datos del otro conductor.',
      "Si tu patrón activó los avisos, le llegan a su celular en cuanto lo registras.",
    ],
  },
  {
    id: "2026-10-04-atencion-patron",
    fecha: "2026-10-05",
    para: "patron",
    requiere: "flotilla",
    titulo: "Accidentes: atender y cancelar",
    resumen: "Marca un accidente como atendido y deja de verlo en tus pendientes.",
    puntos: [
      'En el detalle de un accidente puedes marcarlo como atendido, con una nota si quieres. Sale de "Requieren tu atención" y del escudo.',
      "Si hace falta, lo reabres y vuelve a quedar pendiente. Todo queda en su historial, con fecha y quién lo hizo.",
      "Si se registró por error o de prueba, cancélalo con un motivo. No se borra: queda aparte, en Cancelados.",
      "Los accidentes nuevos siguen pendientes hasta que los atiendas, aunque pasen más de 7 días.",
      "Si tu trabajador confirma después que hubo personas lesionadas y activaste los avisos, te llega otro aviso.",
    ],
  },
  {
    id: "2026-10-04-atencion-trabajador",
    fecha: "2026-10-05",
    para: "trabajador",
    requiere: "flotilla",
    titulo: "Mis accidentes: lo que hizo tu patrón",
    resumen: "Ves si tu patrón ya atendió tu accidente.",
    puntos: [
      'En "Mis accidentes" ves si tu patrón lo marcó como atendido o lo canceló.',
      'Si al registrar contestaste "No lo sé" y después confirmas que hubo personas lesionadas, tu patrón recibe un aviso nuevo, si activó los avisos.',
    ],
  },
  {
    id: "2026-10-05-checador-patron",
    fecha: "2026-10-05",
    para: "patron",
    requiere: null,
    titulo: "Checador: bitácora más completa y mensajes claros",
    resumen: "La bitácora ya imprime la ubicación y la verificación en el aparato de cada entrada.",
    puntos: [
      "Cada día de la bitácora muestra si la entrada fue dentro o fuera de la zona y si se verificó en el aparato (y, si no, por qué).",
      "Si tu trabajador vuelve a entrar el mismo día, ya puede registrarlo; solo se frena un doble toque.",
      "Los errores se explican en palabras, sin códigos.",
    ],
  },
  {
    id: "2026-10-05-checador-trabajador",
    fecha: "2026-10-05",
    para: "trabajador",
    requiere: null,
    titulo: "Checador: reingreso y avisos más claros",
    resumen: "Si vuelves a entrar el mismo día, ya puedes registrarlo.",
    puntos: [
      'Lo que antes se llamaba "biometría" ahora se llama "verificación en el aparato": es la confirmación con tu huella o tu cara en tu teléfono.',
      "Si sales y vuelves a entrar, registra tu entrada: te avisamos antes y tú confirmas.",
      "Si no pudimos cargar tus checadas, te lo decimos y puedes checar igual.",
      "Los mensajes de error te dicen qué pasó y qué hacer, sin códigos.",
    ],
  },
  {
    id: "2026-10-06-checadas-inalterables-patron",
    fecha: "2026-10-06",
    para: "patron",
    requiere: null,
    titulo: "Checador: registros que no cambian",
    resumen: "Cada entrada se califica con el horario que regía ese día.",
    puntos: [
      "Cada entrada se califica con el horario que regía ese día. Si cambias un horario, los días anteriores conservan su calificación.",
      "La plataforma guarda desde cuándo rige cada horario.",
      "Los registros del checador no se pueden modificar ni borrar desde la plataforma.",
      "Si suspendes la cuenta de un trabajador, no entra a la plataforma ni puede checar.",
      "En la ficha verás marcada una checada si el teléfono tenía otro horario al registrarla.",
    ],
  },
  {
    id: "2026-10-06-checadas-inalterables-trabajador",
    fecha: "2026-10-06",
    para: "trabajador",
    requiere: null,
    titulo: "Tu checador",
    resumen: "Tus registros no se pueden modificar ni borrar.",
    puntos: [
      "Tus entradas se califican con el horario que tenías ese día, aunque después cambie.",
      "Tus registros del checador no se pueden modificar ni borrar desde la plataforma.",
    ],
  },
  {
    id: "2026-10-09-correcciones-checadas-patron",
    fecha: "2026-10-09",
    para: "patron",
    requiere: null,
    titulo: "Correcciones anotadas en el checador",
    resumen: "Corrige un registro del checador sin borrar nada: cada corrección queda anotada.",
    puntos: [
      "En la ficha de cada empleado, pestaña Checador: «Corregir» un registro (anularlo o cambiar su tipo), «Agregar registro faltante» de hasta 60 días atrás y «Revertir corrección».",
      "Cada corrección pide motivo, nombre y cargo de quien la hace, y queda anotada con su fecha. No se puede borrar: solo se revierte con otra corrección anotada.",
      "La bitácora quincenal y el registro mensual cuentan los registros como quedaron después de las correcciones y las listan en «Correcciones del periodo».",
      "Tu trabajador ve cada corrección en su checador y puede marcarla «No conforme»; lo verás en su ficha y en la campana.",
      "Si corriges un registro de un periodo ya cerrado, la corrección aparece también en la bitácora y en el registro mensual del periodo en que la hiciste.",
      "Desde el teléfono puedes ver las correcciones y las inconformidades; para hacer una corrección, entra a la plataforma desde una computadora.",
    ],
  },
  {
    id: "2026-10-09-correcciones-checadas-trabajador",
    fecha: "2026-10-09",
    para: "trabajador",
    requiere: null,
    titulo: "Puedes ver y responder las correcciones a tus registros",
    resumen: "Si tu patrón corrige un registro de tu checador, lo ves con su anotación.",
    puntos: [
      "Al abrir tu portal te avisamos cuando tu patrón corrige un registro de tu checador.",
      "En tu checador ves cada corrección: quién la hizo, su cargo, cuándo y por qué.",
      "Si no estás de acuerdo, toca «No conforme» y escribe por qué. Tu inconformidad queda anotada con su fecha y tu patrón la ve.",
      "Las correcciones y tus inconformidades también aparecen en tu bitácora quincenal y en tu registro mensual cuando tu patrón te los entrega o te los envía a «Mis reportes».",
    ],
  },
  {
    id: "2026-10-12-sedes-patron",
    fecha: "2026-10-12",
    para: "patron",
    requiere: null,
    titulo: "Sedes y razones sociales por trabajador",
    resumen: "Cada trabajador checa contra su lugar de trabajo.",
    puntos: [
      "Da de alta las sedes de tu empresa con su ubicación en el mapa y un radio de 100, 200, 300 o 500 m (Empleados → Sedes).",
      "Asigna a cada trabajador su sede y su razón social desde su ficha, a partir de hoy; o una sede a varios a la vez. El historial queda guardado.",
      "Las checadas anteriores conservan la sede contra la que se midieron, y la bitácora y el registro mensual dicen si la sede o la razón social cambiaron en el periodo.",
      "Si tu cuenta maneja más de una razón social, dalas de alta en Datos de mi empresa: los documentos de cada trabajador salen a nombre de la suya.",
      "Para que el teléfono de un trabajador conozca su sede y su horario propio, su cuenta debe estar vinculada a su ficha. En Empleados verás cuántos faltan.",
      "Checar nunca se bloquea por estar fuera: solo se marca.",
    ],
  },
  {
    id: "2026-10-12-sedes-trabajador",
    fecha: "2026-10-12",
    para: "trabajador",
    requiere: null,
    titulo: "Tu checador ahora mide contra tu lugar de trabajo",
    resumen: "Tu checada se compara con la sede que te asignó tu patrón.",
    puntos: [
      "Cuando tu patrón registra tu lugar de trabajo, ves su nombre en tu checador.",
      "Si checas fuera, tu checada se registra igual: solo queda marcada.",
    ],
  },
  {
    id: "2026-10-13-recordatorio-patron",
    fecha: "2026-10-13",
    para: "patron",
    requiere: null,
    titulo: "Recordatorio de entrada para tu equipo",
    resumen: "Un aviso al teléfono de cada trabajador 5 minutos antes de su entrada.",
    puntos: [
      "Enciéndelo en Empleados → Horario y ubicación de trabajo. Viene apagado.",
      "Les llega solo a quienes tienen su cuenta vinculada a su ficha y activaron los avisos en su teléfono, los días que les toca trabajar.",
      "Al encenderlo, empieza al día siguiente; al apagarlo, se detiene de inmediato.",
      "No sustituye la obligación de checar.",
    ],
  },
];

exports.NOVEDADES = NOVEDADES;
